import { ENVIRONMENT } from "@helper/getEnvVariables";

export const isArrayWithElements = (payload: unknown): payload is unknown[] => {
  return Array.isArray(payload) && payload.length >= 1;
};

export const getCookieByName = (name: string): string | undefined => {
  if (typeof window === "undefined") {
    return;
  }
  const match = document.cookie.match(new RegExp(name + "=([^;]+)"));
  return match ? match[1] : undefined;
};

export const isSameOrigin = (origin: string): boolean => {
  if (origin === "http://localhost:3000") return true;
  const matches = origin.match(/([^\\/.]+)\.com.sg/);
  if (matches && matches.length > 1) {
    return matches[1] === "tamilmurasu";
  }
  return false;
};

export const sanitizeSearchKeyword = (searchValue: string) => {
  const sanitizedInput = searchValue.replace(/[^\w\s]/gi, "").substring(0, 255);
  return sanitizedInput;
};

export const prefixCheck = (url: string): boolean => {
  if (url.startsWith("/") && !url.includes("://")) {
    return true;
  }
  return false;
};

export const getWindowHref = (): string => {
  if (typeof window !== "undefined") {
    return window.location.href;
  }
  return getEnvOrigin();
};

export const getWindowOrigin = (): string => {
  if (typeof window !== "undefined") {
    return window.location.origin;
  }
  return getEnvOrigin();
};

export const getEnvOrigin = (): string => {
  switch (ENVIRONMENT) {
    case "dev":
      return "https://web2-dev.tamilmurasu.com.sg";
    case "stg":
      return "https://uat.tamilmurasu.com.sg";
    case "prd":
      return "https://www.tamilmurasu.com.sg";
    default:
      return "http://localhost:3000";
  }
};

//iteration determine how frequent it would return true, useful for ads.
export const displayAfter = (index: number, iteration: number): boolean => {
  return index > 0 && index % iteration === 0;
};

//iteration determine how frequent it would return true, useful for ads.
export const hideAfter = (index: number, iteration: number): boolean => {
  return index > 0 && index % iteration != 0;
};

export const isClientSide: boolean = typeof window !== "undefined";
export const isProd = "https://www.tamilmurasu.com.sg/" === getWindowOrigin();

/**
 * Description of the function.
 *
 * @param {Type} callback - Callback for this function.
 * @param {Type} maxRetries - No. of tries wanted to do.
 * @param {Type} initialDelayMs - Initial delay for each retry.
 * @param {Type} maxDelayMs - Maximum allowed delay.
 */

export async function fetchWithRetry<T>(
  callback: () => Promise<T>,
  maxRetries: number,
  initialDelayMs: number,
  maxDelayMs: number,
): Promise<T> {
  let retries = 0;
  let delay = initialDelayMs;

  while (retries < maxRetries) {
    try {
      return await callback();
    } catch (error) {
      retries++;
      if (retries >= maxRetries) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, delay));
      delay = Math.min(delay * 2, maxDelayMs);
    }
  }

  throw new Error("Max retries exceeded");
}

export function getValidEmailRegex() {
  return /^([a-zA-Z0-9_.+-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
}

export const getLevel1Route = (url: string) => {
  const path = new URL(url).pathname;
  const pathParts = path.split("/");
  const filteredPathParts = pathParts.filter((part) => part.trim() !== "");

  if (filteredPathParts.length > 0) {
    return filteredPathParts[0];
  } else {
    return null;
  }
};

export function isKeyExistsInEnum(CacheKey: string, enumObj: object): boolean {
  return Object.values(enumObj).includes(CacheKey);
}

/**
 * Helper function to check if URL is a URL from the specified from parameter.
 * @param url URL to check.
 * @returns {boolean}
 */
export const isThisUrlFrom = (url: string, from: string): boolean => {
  const domain = new URL(url).hostname;
  return domain.includes(from);
};

export function mapArticlesOrder<T>(
  array: T[],
  order: string[],
  key: keyof T,
): T[] {
  array.sort((a, b) => {
    const A = a[key] as unknown as string;
    const B = b[key] as unknown as string;
    return order.indexOf(A) - order.indexOf(B);
  });
  return array;
}

export const extractMonthAndYear = (
  dateString: string,
): { month: string; year: number } => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    throw new Error("Invalid date string");
  }
  const year = date.getFullYear();
  const month = new Intl.DateTimeFormat("ta-IN", { month: "long" }).format(
    date,
  );

  return { month, year };
};

export function getRandomValue() {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  const randomValue = array[0] / 2 ** 32;

  return randomValue;
}

export function parseSortString(sortStr: string | undefined): number[] {
  if (typeof sortStr !== "string") return [];
  return sortStr
    .split(",")
    .map((item) => parseInt(item.trim(), 10))
    .filter((num) => !isNaN(num));
}

export function parseSizeInteger(
  input: string | undefined,
  defaultValue: number = 10,
): number {
  if (!input) return defaultValue;
  const num = Number(input);
  return Number.isInteger(num) && num > 0 ? num : defaultValue;
}

export function isValidNumber(size?: number): boolean {
  if (typeof size === "undefined") {
    //We never pass size as it is optional
    return true;
  }
  if (typeof size !== "number" || isNaN(size) || size < 0) {
    //We pass size but not valid number
    return false;
  }
  return true;
}

export const isMobileOrTablet = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile = /iphone|ipad|ipod|android|blackberry|windows phone/g.test(
    userAgent,
  );
  const isTablet = /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/g.test(
    userAgent,
  );

  return isMobile || isTablet;
};

export function reactionCount(count: number): string {
  if (count >= 1_000_000_000) {
    return (count / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B"; // Billions
  } else if (count >= 1_000_000) {
    return (count / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M"; // Millions
  } else if (count >= 1_000) {
    return (count / 1_000).toFixed(1).replace(/\.0$/, "") + "K"; // Thousands
  }

  return count.toLocaleString();
}

/**
 * Formats a Date object into "d-mm-yyyy".
 * @param date - The date to format.
 * @returns A string representing the formatted date.
 */
export function quizFormatDate(date: Date): string {
  return `${date.getDate().toString().padStart(2, "0")}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getFullYear()}`;
}

export function convertDateToISOFormat(date: string): string {
  // Split the date string into day, month, and year
  const [day, month, year] = date.split("-").map(Number);

  // Return the date in yyyy-mm-dd format
  return `${year.toString().padStart(4, "0")}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
}

export function getRandomDate(startDate: Date, endDate: Date): Date {
  const startTimestamp = startDate.getTime();
  const endTimestamp = endDate.getTime();

  if (startTimestamp > endTimestamp) {
    // returning the start date as fallback.
    return new Date(startTimestamp);
  }

  const randomTimestamp = Math.floor(
    Math.random() * (endTimestamp - startTimestamp + 1) + startTimestamp,
  );
  return new Date(randomTimestamp);
}
